import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { VideoPlayer, VideoPlayerEvent } from "@telus/opus-component-library/video-player";
import { formatSecondsToTimeDisplay, isFullScreen, hasADAudioTrack } from "../../shared/utils/playerUtils";
import PlayerConstants from "../../shared/constants/player";
import Document from "../../shared/document";
import { getLocalStorage, setLocalStorage } from "../../shared/utils/localStorage";
import { getSessionStorage, removeSessionStorage } from "../../shared/utils/sessionStorage";
import { handleImageError } from "../../shared/utils/image";
import { useDispatch } from "react-redux";
import { showPlaybackError } from "../../App/state/actions";
import useOutsideClickIndicator from "../../shared/hooks/useOutsideClickIndicator";
import { IsSafari, mapPlayerEvent } from "../../shared/utils/index";
import { PlayerEvent } from "../../shared/constants/analytics";
import ProgressBar from "../ProgressBar";
import constants from "../../shared/constants";
import PlaybackError from "../PlaybackError";
import ToastNotification from "../ToastNotification";
import VODToolTip from "../VODToolTip";

import {
  trackMediaBufferComplete,
  trackMediaBufferStart,
  trackMediaComplete,
  trackMediaPause,
  trackMediaSeek,
  trackMediaSeeked,
  trackPlayheadChange,
} from "../../shared/analytics/media";
import { trackGenericAction } from "../../shared/analytics/dataLayer";
import { ANALYTICS_EVENT_TYPES, LINK_INFO } from "../../shared/constants/analytics";
import telusConvivaAnalytics from "../../shared/utils/convivaAnalytics";
import useSetInterval from "../../shared/hooks/useSetInterval";
import storageConstants from "../../shared/constants/storage";

const { PLAYER_CONTROLS, PLAYER_LIFECYCLE_ERROR_CODES } = PlayerConstants;
const { SETTINGS_CONFIG, TOOLTIP_THRESHOLD_TIME } = constants;

const isSafari = IsSafari();
const placeholder = process.env.PUBLIC_URL + "/images/swimlane-landscape-324px.jpg";
const defaultChannelLogoIcon = process.env.PUBLIC_URL + "/images/default-channel-logo.png";
const { IS_FRESH_LOGIN, PLAYER_LOADED, HAS_SEEN_VOD_TOOLTIP, TOOL_TIP_SESSION_START } = storageConstants;

/** import modules */
import { usePlayerState } from "./core/playerState";
import { usePlayerVars, usePlayerClassNames } from "./core/playerPropVars";
import {
  usePlayerActionUtils,
  isOnDemandRestartButtonVisible,
  generateEpisodeInfoString,
  getCurrentTimestamp,
  stopAutoPlayHandler,
  calculateVodPos,
  handleOnDemandProgressFastForward,
  handleOnDemandProgressRewind,
  scheduleRetry,
  handleRetry,
  shouldRetryByStream,
} from "./core/playerUtils";
import {
  playerExists,
  playerGetDuration,
  playerGetCurrentPlaybackTime,
  playerCheckDynamicAsset,
  playerHandleBookmarkResume,
  playerHandleLoadPlaybackSuccess,
  playerIsPlaying,
  playerHandleADToggleChange,
  shakaBuildPlayerSourceConfig,
} from "./playerHelpers/playerCoreUtils";
import { playerStartOverStreamHelper } from "./playerHelpers/playerSocuUtils";
import usePlayerEventHandlers from "./playerHelpers/playerEventHandlers";
import { shakaHandleLicenseRequest, shakaHandleManifestResponse } from "./playerHelpers/shakaNetworkConfig";
import { playerGetPlayedTimeForAnalytics } from "./playerHelpers/playerAnalytics";
import {
  playerGetBingeWatchCountdownTime,
  playerHandleStaticSeeking,
  playerGetSeekParameters,
  playerCalculateSeekPosition,
  playerSeekableEndPos,
} from "./playerHelpers/playerVodUtils";
import { playerBuildBookmark } from "./playerHelpers/playerStopContent";
import {
  shakaInitPlayerVolume,
  shakaHandlePlayPauseButton,
  shakaSetVolume,
  shakaSetVideoPlayerControlState,
} from "./playerHelpers/shakaPlayerControls";
import {
  usePlayerMountEffect,
  useReloadAppEffect,
  useContentIdEffect,
  usePCBlockEffect,
  useFocusOnSideNavOpen,
  useStillWatchingPromptEffect,
} from "./core/playerCoreEffects";
import {
  handleVideoControlsMouseMove,
  handleClickToggleState,
  handleVideoControlsKeyUp,
  handleVolumeKeyUp,
  handleRewindButton,
  handleForwardButton,
  handleVolumeButtonHover,
  handleVolumeSlideChange,
  toggleSettingVisibility,
  handleVolumeToggle,
  handleClosePlayer,
  handleFullScreenToggle,
} from "./core/playerControls";
import { usePlayerAnalytics } from "./core/analytics";
import { usePlayerUnmount, useUnloadPlayer, useInitPlayerControls } from "./effectModules/playerCoreEffects";
import { useBingeWatchEffect, useNextEpisodeCardEffect } from "./core/bingeWatch";
import { updateBookmarkAPI, createBookmarkAPI } from "./core/bookmark";
import { usePlaybackErrorEffect } from "./core/errorHandling";
import PlayerWrapper from "./components/PlayerWrapper";
import ShakaPlayerContainer from "./components/ShakaPlayerContainer";
import BackgroundImage from "./components/BackgroundImage";
import GradientBackground from "./components/GradientBackground";
import ClosedCaption from "./components/ClosedCaption";
import StillWatchingCloseIcon from "./components/StillWatchingCloseIcon";
import FullScreenToggle from "./components/FullScreenToggle";
import SidebarToggle from "./components/SidebarToggle";
import CloseButton from "./components/CloseButton";
import ChannelInfo from "./components/ChannelInfo";
import Details from "./components/Details";
import VolumeControl from "./components/VolumeControl";
import SettingsControl from "./components/SettingsControl";
import RewindButton from "./components/RewindButton";
import PlayPauseButton from "./components/PlayPauseButton";
import ForwardButton from "./components/ForwardButton";
import RestartButton from "./components/RestartButton";
import NextEpisodeButton from "./components/NextEpisodeButton";
import NextEpisodeCard from "./components/NextEpisodeCard";
import StillWatchingCard from "./components/StillWatchingCard";
import useCurrentTime from "../../shared/hooks/useCurrentTime.js";
import "./style.scss";

/**
 * Video player that streams AMC content for VOD and Live streams. Works with AMC's ericsson DRM
 * @component
 * @param {object} props - StreamInfo, MetadataInfo, style and type ( VOD or Live )
 */
function VodPlayer(props) {
  const {
    className,
    type,
    currentContentId,
    playbackType,
    onPlaybackStateChange,
    control,
    onFullScreenClick,
    showListButton,
    handleListIconClick,
    resumeTimeSeconds,
    isSideNavOpen,
    isReadyCallback,
    setIsReadyToChangeStreams,
    showStillWatchingPrompt,
    restartUserInactivityTimer,
    updateVodProgress,
    streamInfo,
    hideListChannel,
    getMediaNodeMetaData = () => null,
    triggerAnalyticsCalls = () => {},
    isProgramChangedRef,
    contentType,
    contentLockedOverlay = null,
    parentalPin = null,
    bookmark,
    triggerRestart,
    isRestartedStream,
    isReadyToChangeStreams,
    resetPlayerPage,
    updateSeasonEntitlements,
    initPlaybackTimestamp,
    dtContentType,
    convivaContentSubType,
    isPlayerControlsDisable,
    PCBlocked,
    userProfile,
  } = props;
  const { appProvider, toastData, playbackError, spinningLoaderParams, reloadAppFlag, featureToggles } =
    usePlayerState();
  const {
    assetId,
    externalContentId,
    seriesDetails,
    title,
    subTitle,
    image,
    channelNumber,
    bgImage,
    extendedMetadata,
    nextProgram,
    nextEpisodePoster,
    loadNextProgram,
  } = usePlayerVars(props);
  const { toggleSpinningLoader, handleStopContent, managePlaybackDeltaTimer } = usePlayerActionUtils();
  const {
    playerInitOnDemandBookmark,
    playerInitFFStatus,
    playerInitRWStatus,
    handleRetryAndPlayerError,
    shakaInitPlaybackSettings,
    shakaInitADSetting,
    shakaInitCCSetting,
    shakaSetVodProgress,
    playerHandleProgressUpdate,
    shakaHandleQualityChange,
    shakaHandleSubtitleChange,
  } = usePlayerEventHandlers();
  const isFreshLogin = getLocalStorage(IS_FRESH_LOGIN);
  const playerLoaded = getSessionStorage(PLAYER_LOADED);
  const initialVolume = getLocalStorage(PLAYER_CONTROLS.VOLUME);
  const isPlayerMute = getLocalStorage(PLAYER_CONTROLS.MUTE) === "true" ? true : false;
  const closeCaptionSetting = getLocalStorage(SETTINGS_CONFIG.CLOSED_CAPTION);
  const dvSetting = getLocalStorage(SETTINGS_CONFIG.DESCRIBED_VIDEO);
  const muteSetting = getLocalStorage(SETTINGS_CONFIG.MUTE);
  const { isConvivaAppTrackerEnabled, isPlaybackRetry, isVODZoetropeEnabled, isDynatraceTrackingEnabled } =
    featureToggles;

  const history = useHistory();
  const dispatch = useDispatch();

  const [settingsDropdownVisibility, setSettingsDropdownVisibility] = useState(false);
  const [isMuted, setIsMuted] = useState(isPlayerMute);
  const [volumeLevel, setVolumeLevel] = useState(initialVolume ? initialVolume : 0);
  const [volumeVisibility, setVolumeVisibility] = useState(false);
  const [videoControlsStyle, setVideoControlsStyle] = useState({});
  const [playPauseIcon, setPlayPauseIcon] = useState("/images/playback-pause.svg");
  const [progressStyle, setProgressStyle] = useState({});
  const [elapsedTime, setElapsedTime] = useState("");
  const [remainingTime, setRemainingTime] = useState(null);
  const [showNextEpisodeCard, setShowNextEpisodeCard] = useState(false);
  const [playNextProgram, setPlayNextProgram] = useState(true);
  const [startTime, setStartTime] = useState(props.metadataInfo?.startTime?.programStartTime);
  const [endTime, setEndTime] = useState(
    props.metadataInfo?.startTime?.programStartTime + props.metadataInfo?.duration * 1000
  );
  const [isADAvailable, setADAvailable] = useState(false);
  const [isFFEnabled, setFFEnabled] = useState(false);
  const [isRWEnabled, setRWEnabled] = useState(false);
  const [ffErrorStatus, setFFErrorStatus] = useState(true);
  const [bingeWatchInProgress, setBingeWatchInProgress] = useState(false);
  const [bingeWatchTimer, setBingeWatchTimer] = useState(0);
  const [showBgImage, setShowBgImage] = useState(true);
  const [ccList, setCCList] = useState([]);
  const [isCCAvailable, setIsCCAvailable] = useState(false);
  const [userHasClick, setUserHasClick] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const [videoSource, setVideoSource] = useState(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [currentImageTrack, setCurrentImageTrack] = useState(null);
  const [isSourceLoaded, setIsSourceLoaded] = useState(false);
  const [showErrorOverlay, setShowErrorOverlay] = useState(false);
  const playerMgr = useRef(null);
  const isClosedCaptionEnabled = useRef(closeCaptionSetting ?? false);
  const isDescribedVideoEnabled = useRef(dvSetting ?? false);
  const ccRef = useRef([]);
  const volumeRef = useRef(null);
  const settingsRef = useRef(null);
  const videoCCContainerRef = useRef(null);
  const videoWrapper = useRef(null);
  const videoControlsTimer = useRef(null);
  const autoPlayTimer = useRef(null);
  const bingeWatchCard = useRef(null);
  const stillWatchingCard = useRef(null);
  const playButtonRef = useRef(null);
  const volumeButtonRef = useRef(null);
  const volumeBarRef = useRef(null);
  const bookmarkTimer = useRef(null);
  const { t: translate } = useTranslation();
  const playbackDeltaTimer = useRef(null);
  const playbackTimeDelta = useRef(0); // how many seconds of actual playback has occurred in a player session
  const activeContentId = useRef(currentContentId);
  const isDynamicRecordingType = useRef(false);
  const hasCalledStopContent = useRef(false);
  const leftMargin = useRef(null);
  const isPauseLiveAllowed = useRef(false); // boolean ref to act as a check for pltv
  const isRestartTrickPlayAllowed = useRef(false); // boolean ref to act as a check for restart and lookback trickplay (specifically ability to skip forward)
  const isClosingPlayer = useRef(false); // boolean ref to act as a check if user has opted to close the video player, need this to avoid unnecessary stream transitions
  const startingOverStream = useRef(false); // boolean ref to act as a check if restarting of a live stream is in progress
  const isTimeShifting = useRef(false); // boolean ref to track if a live stream or a start over stream timeshift is in progress
  const isSeeking = useRef(false); // boolean ref to track if a stream seek is in progress
  const blockPlayback = useRef(false);
  const streamPlaybackType = useRef(null); // ref to store the playback type of the loaded stream
  const cancelTokenSource = useRef(axios.CancelToken.source()); // cancelTokenSource ref for requests unmount clean up
  const bookmarkCreated = useRef(bookmark);
  const activeExtendedMetadata = useRef(extendedMetadata);
  const retryTimer = useRef(null);
  const retryTsValue = useRef();
  const isNextLiveStreamFetched = useRef(false); // ref to track if the details of next live stream in queue are fetched
  const videoProgressMade = useRef(false);
  const retryInPlace = useRef(false);
  const shakaIsPlaybackStarted = useRef(false);
  const shakaKeyRotationToken = useRef(null);
  const shakaKeyRotationExpiryTimeout = useRef(null);
  const featureTogglesRef = useRef(featureToggles);
  featureTogglesRef.current = featureToggles;
  const currentTime = useCurrentTime(3600000, true);

  const spinnerToggle = spinningLoaderParams?.toggle;
  const { playerLoaderClassName, videoClassName, videoControlsName } = usePlayerClassNames(className);

  const intervalTicker = useSetInterval(1000);
  const videoExitThreshold = 1; // Due to the unpredictable nature of recordings we need to increase this threshold value to allow player exit
  const isPlayheadAvailable = isFFEnabled && isRWEnabled;
  const cdnUrl = useRef(null);
  const getPlayheadCallback = useCallback(() => {
    return playerGetPlayedTimeForAnalytics(isPlayheadAvailable, playerMgr);
  }, [isPlayheadAvailable]);

  const tooltipMessages = [
    {
      tooltipHeader: translate("onboarding_zoetrope_title_web"),
      tooltipText: translate("onboarding_zoetrope_desc_web"),
      position: "progressbar",
    },
  ];

  // Calculating remaining time when stream is past 98 percent of it's duration
  const bingeWatchCountdownTime = playerGetBingeWatchCountdownTime(true, false, playerMgr);

  const isDynamicAsset = () => {
    return playerCheckDynamicAsset(playerMgr);
  };

  /**
   * Builds the bookmark object that should be passed in the bookmark & stop content API payloads
   *
   * @param {Boolean} isUpdatingBookmark Flag used to differentiate between create & update bookmark structures
   * @returns {Object} object with details to create/update a bookmark
   */
  const buildBookmarkCallback = useCallback(
    (isUpdatingBookmark = false) => {
      return playerBuildBookmark(
        true,
        playerMgr,
        null,
        videoExitThreshold,
        null,
        type,
        playbackType,
        externalContentId,
        bookmarkCreated,
        activeExtendedMetadata,
        isUpdatingBookmark
      );
    },
    [videoExitThreshold, type, playbackType, externalContentId]
  );

  /**
   * Function to make the Stop Content call for AVS backend. If current content is VOD,
   * save bookmark info too.
   */
  const handleAVSStopContent = useCallback(
    (playerError = false, newContentId = null) => {
      shakaIsPlaybackStarted.current = false;
      setIsSourceLoaded(false);
      return handleStopContent({
        appProvider,
        activeContentId,
        hasCalledStopContent,
        playbackType,
        playbackTimeDelta,
        buildBookmarkCallback,
        playerLoaderClassName,
        isRestartedStream,
        isLookbackStream: false,
        liveProgramDetails: null,
        setIsReadyToChangeStreams,
        exitLookbackMode: false,
        changeLookbackStream: null,
        switchLive: () => {},
        type,
        updateSeasonEntitlements,
        startingOverStream,
        isClosingPlayer,
        playerError,
        newContentId,
      });
    },
    [
      handleStopContent,
      appProvider,
      playbackType,
      buildBookmarkCallback,
      playerLoaderClassName,
      isRestartedStream,
      setIsReadyToChangeStreams,
      type,
      updateSeasonEntitlements,
    ]
  );

  // function to stop the current dynamic stream and direct player to restart the stream
  const startOverStream = useCallback(
    (transitionLookbackStream = false) => {
      playerStartOverStreamHelper(
        type,
        false,
        transitionLookbackStream,
        playerMgr,
        setIsReadyToChangeStreams,
        handleAVSStopContent,
        startingOverStream,
        triggerRestart,
        null,
        null
      );
    },
    [handleAVSStopContent, setIsReadyToChangeStreams, triggerRestart, type]
  );

  const {
    propsRef,
    trackPlaybackStartTime,
    loadNextEpisodeCallback,
    trackConvivaAppTrackerEvent,
    trackCloseClick,
    hasBookmark,
    closedCaption,
    rwCount,
    ffCount,
    trackMediaStop,
    upNext,
    describedVideo,
  } = usePlayerAnalytics(props, convivaContentSubType, triggerAnalyticsCalls, isRestartedStream, bookmark, false);

  /**
   * Helper function to start or stop the playback delta timer.
   * The playback delta timer is used to track the number of playback seconds that occur
   * in a player session.
   * @param {Boolean} shouldStart
   */
  const controlPlaybackDeltaTimer = (shouldStart) => {
    managePlaybackDeltaTimer(playbackDeltaTimer, playbackTimeDelta, shouldStart);
  };
  usePlayerMountEffect({
    isOnDemandType: true,
    playbackType,
    setPlayNextProgram,
    playButtonRef,
    volumeButtonRef,
    playerLoaded,
    isFreshLogin,
    cancelTokenSource,
    bookmarkTimer,
    videoControlsTimer,
    autoPlayTimer,
    controlPlaybackDeltaTimer,
    handleAVSStopContent,
    setCCList,
  });

  useReloadAppEffect(reloadAppFlag, appProvider);

  const handleOutsideVolumeClick = () => {
    setVolumeVisibility(false);
  };
  useOutsideClickIndicator(volumeRef, handleOutsideVolumeClick);

  const handleOutsideSettingsClick = () => {
    setSettingsDropdownVisibility(false);
  };
  useOutsideClickIndicator(settingsRef, handleOutsideSettingsClick);

  useContentIdEffect({
    controlPlaybackDeltaTimer,
    handleAVSStopContent,
    hasCalledStopContent,
    activeContentId,
    currentContentId,
    isSafari,
    getSessionStorage,
    setUserHasClick,
  });

  usePCBlockEffect(PCBlocked, blockPlayback);

  useUnloadPlayer(playerMgr, currentContentId);

  usePlayerUnmount(playerMgr);

  // Player retry for fetching a new JWT and manifest
  const performRetry = async () => {
    await handleRetry(
      appProvider,
      contentType,
      currentContentId,
      assetId,
      parentalPin,
      retryTimer,
      toggleSpinningLoader,
      shakaInitializePlayer,
      createRetryTimer
    );
  };

  // Player retry logic base on offset interval
  const createRetryTimer = () => {
    scheduleRetry(retryTimer, retryTsValue, isPlaybackRetry, performRetry);
  };

  const shakaInitializePlayer = (manifest = null, jwtToken = null) => {
    setShowErrorOverlay(false);
    if (isReadyToChangeStreams) {
      const handleLicenseRequest = (request) => {
        return shakaHandleLicenseRequest(
          request,
          shakaIsPlaybackStarted,
          shakaKeyRotationToken,
          shakaKeyRotationExpiryTimeout,
          contentType,
          appProvider,
          currentContentId,
          assetId,
          parentalPin
        );
      };
      const handleManifestResponse = (response) => {
        return shakaHandleManifestResponse(response, isSafari, false, cdnUrl);
      };
      const manifestUrl = manifest ? manifest : streamInfo.manifestURL;
      const authToken = jwtToken ? jwtToken : streamInfo.authToken;
      const source = shakaBuildPlayerSourceConfig(
        manifestUrl,
        authToken,
        streamInfo.contentId,
        streamInfo.LA_URL,
        streamInfo.LA_CERT_URL,
        handleManifestResponse,
        handleLicenseRequest
      );

      // Report playback origin if it's customer triggered or retry
      telusConvivaAnalytics.reportSessionStart(retryInPlace.current);

      setVideoSource(source);
    }
  };

  useEffect(() => {
    if (!streamInfo) return;
    bookmarkCreated.current = bookmark;
    activeExtendedMetadata.current = extendedMetadata;
    streamPlaybackType.current = playbackType;

    toggleSpinningLoader(true, playerLoaderClassName);
    setBingeWatchInProgress(false);
    setBingeWatchTimer(0);
    setShowBgImage(true);
    autoPlayTimer.current = null;
    setProgressStyle(0);
    if (updateVodProgress) updateVodProgress(0);
    setElapsedTime("");
    setCCList([]);
    setPlayNextProgram(true);
    ccRef.current = [];
    playbackTimeDelta.current = 0;
    isNextLiveStreamFetched.current = false;

    shakaInitializePlayer();

    hasCalledStopContent.current = false;

    setPlayPauseIcon("/images/playback-pause.svg");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamInfo]);

  useEffect(() => {
    if (isVODZoetropeEnabled && videoStarted) {
      const currentUserId = userProfile?.user?.profile?.profileData?.userId;
      let hasSeenTooltip = getLocalStorage(HAS_SEEN_VOD_TOOLTIP);

      if (!hasSeenTooltip) {
        hasSeenTooltip = {};
      }

      if (currentUserId) {
        if (!hasSeenTooltip[currentUserId]) {
          hasSeenTooltip[currentUserId] = { seen: true, sessionStart: currentTime };
          setIsTooltipVisible(true);
          setLocalStorage(HAS_SEEN_VOD_TOOLTIP, hasSeenTooltip);
        } else {
          // Check if it should reappear after 72 hours
          const userSessionStart = hasSeenTooltip[currentUserId].sessionStart || 0;
          if (currentTime >= userSessionStart + TOOLTIP_THRESHOLD_TIME) {
            setIsTooltipVisible(true);
            hasSeenTooltip[currentUserId].sessionStart = currentTime;
            setLocalStorage(HAS_SEEN_VOD_TOOLTIP, hasSeenTooltip);
          }
        }
      }
    }
  }, [userProfile, currentTime, isVODZoetropeEnabled, videoStarted]);

  useInitPlayerControls(playerMgr, control);

  useBingeWatchEffect(
    type,
    playNextProgram,
    nextProgram,
    bingeWatchInProgress,
    remainingTime,
    bingeWatchCountdownTime,
    bingeWatchTimer,
    setBingeWatchTimer,
    setShowNextEpisodeCard,
    videoExitThreshold,
    loadNextProgram,
    loadNextEpisodeCallback,
    autoPlayTimer
  );
  useNextEpisodeCardEffect(showNextEpisodeCard, bingeWatchCard, setVideoControlsStyle);

  useFocusOnSideNavOpen(videoWrapper, isSideNavOpen);

  useStillWatchingPromptEffect(
    showStillWatchingPrompt,
    setShowNextEpisodeCard,
    autoPlayTimer,
    stillWatchingCard,
    setVideoControlsStyle
  );

  useEffect(() => {
    if (intervalTicker > 0 && playerExists()) {
      trackPlayheadChange(isPlayheadAvailable, getPlayheadCallback);
    }
  }, [appProvider, endTime, getPlayheadCallback, intervalTicker, isPlayheadAvailable]);

  const currentPlaybackTime = playerGetCurrentPlaybackTime(playerMgr);

  const updateBookmarkInfo = useCallback(() => {
    return updateBookmarkAPI(
      appProvider,
      type,
      activeContentId.current,
      bookmarkCreated.current?.bookmarkSet?.bookmarkSetId,
      buildBookmarkCallback
    );
  }, [appProvider, type, buildBookmarkCallback]);

  const createBookmarkInfo = useCallback(() => {
    return createBookmarkAPI(appProvider, type, activeContentId.current, buildBookmarkCallback);
  }, [appProvider, buildBookmarkCallback, type]);

  usePlaybackErrorEffect(
    playbackError,
    translate,
    getMediaNodeMetaData,
    type,
    isConvivaAppTrackerEnabled,
    activeContentId,
    hasCalledStopContent
  );

  let initPlayerTimestamp = useRef(null);

  /**
   * Shaka player event handlers start
   */
  const handleSourceLoadingEvent = (event) => {
    // Set the initial timestamp for the player init metrics
    initPlayerTimestamp.current = getCurrentTimestamp();
  };

  const handleSourceLoadedEvent = async (event) => {
    shakaInitPlaybackSettings(
      type,
      currentContentId,
      assetId,
      videoProgressMade,
      isSafari,
      playerMgr,
      setShowBgImage,
      controlPlaybackDeltaTimer
    );
    // Apply CC settings on source load (not Safari) - don't track as user action
    if (!isSafari) onSubTitleChange(isClosedCaptionEnabled.current, false);
    isReadyCallback && isReadyCallback();
    playerHandleLoadPlaybackSuccess(propsRef, props, cdnUrl, playerMgr);
    playerMgr.current.play();
    setCurrentImageTrack(playerMgr.current.getImageTracks());
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.SourceLoaded),
      playbackError,
      resetPlayerPage
    );
    setIsSourceLoaded(true);
  };
  // Seek to the bookmarked position if the bookmark is available and source is loaded. It needs to be in separate useEffect to avoid race condition
  useEffect(() => {
    if (isSourceLoaded && resumeTimeSeconds) {
      playerHandleBookmarkResume(playerMgr, resumeTimeSeconds, null);
    }
  }, [resumeTimeSeconds, isSourceLoaded]);

  const handleReadyEvent = (event) => {
    shakaInitPlayerVolume(playerMgr, volumeLevel, muteSetting, setIsMuted, setVolumeLevel);
    playerInitOnDemandBookmark(
      playbackType,
      true,
      bookmarkTimer,
      bookmarkCreated,
      updateBookmarkInfo,
      createBookmarkInfo,
      hasBookmark
    );
    playerInitFFStatus(
      true,
      null,
      setVideoStarted,
      activeExtendedMetadata,
      setFFEnabled,
      isRestartTrickPlayAllowed,
      setFFErrorStatus,
      isFFEnabled
    );
    playerInitRWStatus(true, activeExtendedMetadata, setRWEnabled, null);
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.Ready),
      playbackError,
      resetPlayerPage
    );
  };

  const handlePlayingEvent = (event) => {
    if (!shakaIsPlaybackStarted.current) {
      toggleSpinningLoader(false);
      if (isSafari) {
        setTimeout(() => {
          shakaInitADSetting(playerMgr, isDescribedVideoEnabled, setADAvailable, hasADAudioTrack);
          shakaInitCCSetting(playerMgr, setIsCCAvailable, isClosedCaptionEnabled);
        }, [1000]);
      } else {
        shakaInitADSetting(playerMgr, isDescribedVideoEnabled, setADAvailable, hasADAudioTrack);
        shakaInitCCSetting(playerMgr, setIsCCAvailable, isClosedCaptionEnabled);
      }
      shakaIsPlaybackStarted.current = true;
      telusConvivaAnalytics.onPlaybackStateChanged(
        propsRef,
        playerMgr.current,
        mapPlayerEvent(PlayerEvent.Playing),
        playbackError,
        resetPlayerPage
      );
      trackPlaybackStartTime(
        initPlaybackTimestamp,
        initPlayerTimestamp,
        dtContentType,
        isDynatraceTrackingEnabled,
        retryInPlace.current
      );
      retryInPlace.current = false;
    }
  };

  const handlePausedEvent = (event) => {
    toggleSpinningLoader(false);
    controlPlaybackDeltaTimer(false);
    trackMediaPause();
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.Paused),
      playbackError,
      resetPlayerPage
    );
  };

  const handlePlaybackFinishedEvent = (event) => {
    controlPlaybackDeltaTimer(false);
    removeSessionStorage(PLAYER_LOADED);
    trackMediaComplete();
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.PlaybackFinished),
      playbackError,
      resetPlayerPage
    );
  };

  const handleTimeChangedEvent = (event) => {
    if (blockPlayback.current) {
      shakaSetVideoPlayerControlState(playerMgr.current, PLAYER_CONTROLS.PAUSE);
    }
    playerHandleProgressUpdate(
      event,
      playerMgr,
      isRestartedStream,
      setElapsedTime,
      setRemainingTime,
      isDynamicAsset,
      endTime,
      props,
      setProgressStyle,
      updateVodProgress
    );
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.TimeChanged),
      playbackError,
      resetPlayerPage
    );
    // Unfortunately this is the only way we can prevent CC from showing up when launching HLS.
    // TrackText can't be control until content is playing for HLS.
    // Pass false to NOT track this as a user action (Safari HLS workaround)
    if (isSafari) onSubTitleChange(isClosedCaptionEnabled.current, false);
  };

  const handleCaptionTrackAddedEvent = (event) => {
    if (event.subtitle?.id && isClosedCaptionEnabled.current) {
      closedCaption.current = 1;
    }
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.SubtitleAdded),
      playbackError,
      resetPlayerPage
    );
  };

  const handleSeekingEvent = (event) => {
    controlPlaybackDeltaTimer(false);
    toggleSpinningLoader(true, playerLoaderClassName);
    if (event && event.time) {
      isSeeking.current = true;
      shakaSetVodProgress(
        event.time,
        playerMgr,
        isRestartedStream,
        setElapsedTime,
        setRemainingTime,
        setProgressStyle,
        updateVodProgress
      );
    }
    trackMediaSeek();
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.Seek),
      playbackError,
      resetPlayerPage
    );
  };

  const handleSeekedEvent = (event) => {
    controlPlaybackDeltaTimer(playerIsPlaying(playerMgr));
    toggleSpinningLoader(false);
    isSeeking.current = false;
    trackMediaSeeked();
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.Seeked),
      playbackError,
      resetPlayerPage
    );
  };

  const handleBufferingEvent = (event) => {
    toggleSpinningLoader(true, playerLoaderClassName);
    trackMediaBufferStart();
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.StallStarted),
      playbackError,
      resetPlayerPage
    );
  };

  const handleBufferedEvent = (event) => {
    toggleSpinningLoader(false);
    trackMediaBufferComplete();
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      mapPlayerEvent(PlayerEvent.StallEnded),
      playbackError,
      resetPlayerPage
    );
  };

  const handleVideoQualityChanged = (event) => {
    const shakaStats = playerMgr.current.getFullStats();
    shakaHandleQualityChange(event, playerMgr);
    telusConvivaAnalytics.onPlaybackStateChanged(
      propsRef,
      playerMgr.current,
      {
        type: PlayerEvent.VideoPlaybackQualityChanged,
        targetQuality: { bitrate: shakaStats?.streamBandwidth, height: shakaStats?.height, width: shakaStats?.width },
      },
      playbackError,
      resetPlayerPage
    );
  };

  // Used to determine if retry allowed, specifically playback types can be in panic state
  const isRetryPlayback = () => {
    return shouldRetryByStream(false, true, false, false, false, false, featureTogglesRef.current);
  };

  const handlePlaybackError = (event) => {
    console.error("[Player] player error event = ", event);
    if (PLAYER_LIFECYCLE_ERROR_CODES.includes(event?.code)) return;
    shakaIsPlaybackStarted.current = false;
    retryInPlace.current = true;
    setVideoSource(null);
    handleRetryAndPlayerError(
      isPlaybackRetry,
      videoProgressMade,
      playerLoaderClassName,
      playerMgr,
      createRetryTimer,
      setShowBgImage,
      controlPlaybackDeltaTimer,
      ffErrorStatus,
      event,
      setFFErrorStatus,
      onPlaybackStateChange,
      isRetryPlayback,
      handleAVSStopContent,
      activeContentId.current
    );
    if (event.severity === 2) {
      setShowErrorOverlay(true);
      if (!playbackError || Object.values(playbackError).every((value) => value === null)) {
        dispatch(showPlaybackError(event.code, event.message));
      }
      telusConvivaAnalytics.onPlaybackStateChanged(
        propsRef,
        playerMgr.current,
        {
          type: PlayerEvent.Error,
          message: event.message,
          code: event.code,
        },
        playbackError,
        resetPlayerPage
      );
    }
  };

  const handleWarningEvent = (event) => {
    console.warn("[Player] player warning event = ", event);
  };

  /**
   * Shaka player event handlers end
   */

  // DOM Event Handlers
  const videoControlsMouseMove = (e) => {
    handleVideoControlsMouseMove(
      setVideoControlsStyle,
      videoControlsTimer,
      setVolumeVisibility,
      setSettingsDropdownVisibility,
      videoWrapper
    );
  };
  //Click Events Handler
  const onClickToggleState = (event) => {
    handleClickToggleState(
      event,
      type,
      null,
      spinnerToggle,
      playPauseButtonHandler,
      onFullScreenButtonHandler,
      signalUserActivity
    );
  };
  const videoControlsKeyUp = (event) => {
    handleVideoControlsKeyUp(
      event,
      spinnerToggle,
      showNextEpisodeCard,
      showStillWatchingPrompt,
      videoControlsMouseMove,
      true,
      null,
      isRWEnabled,
      setVolumeVisibility,
      rewindButtonHandler,
      isFFEnabled,
      null,
      isRestartedStream,
      forwardButtonHandler,
      playPauseButtonHandler
    );
  };

  const handleVolume = (event) => {
    handleVolumeKeyUp(
      event,
      showNextEpisodeCard,
      showStillWatchingPrompt,
      videoControlsMouseMove,
      volumeBarRef,
      setVolumeVisibility,
      settingsDropdownVisibility,
      setSettingsDropdownVisibility,
      onFullScreenButtonHandler,
      volumeButtonHandler
    );
  };

  const playPauseButtonHandler = (event) => {
    shakaHandlePlayPauseButton(playerMgr, shakaSetVideoPlayerControlState, setPlayPauseIcon, signalUserActivity);
  };

  const rewindButtonHandler = (event) => {
    handleRewindButton(videoWrapper, toggleSpinningLoader, playerLoaderClassName, seekPlayback, signalUserActivity);
    trackConvivaAppTrackerEvent(ANALYTICS_EVENT_TYPES.REWIND, getMediaNodeMetaData);
    rwCount.current++;
  };

  const forwardButtonHandler = (event) => {
    handleForwardButton(
      videoWrapper,
      toggleSpinningLoader,
      playerLoaderClassName,
      seekPlayback,
      signalUserActivity,
      true
    );
    trackConvivaAppTrackerEvent(ANALYTICS_EVENT_TYPES.FAST_FORWARD, getMediaNodeMetaData);
    ffCount.current++;
  };
  const settingButtonHandler = (event) => {
    toggleSettingVisibility(
      setVolumeVisibility,
      setSettingsDropdownVisibility,
      settingsDropdownVisibility,
      signalUserActivity
    );
  };

  const volumeButtonHandler = (event) => {
    handleVolumeToggle(
      userHasClick,
      setUserHasClick,
      setSettingsDropdownVisibility,
      setVolumeVisibility,
      event,
      isMuted,
      shakaSetVolume,
      playerMgr,
      setVolumeLevel,
      initialVolume,
      setIsMuted,
      signalUserActivity
    );
  };

  const volumeButtonHoverHandler = (event) => {
    handleVolumeButtonHover(setSettingsDropdownVisibility, setVolumeVisibility, volumeBarRef);
  };

  const onVolumeSlideChange = (input) => {
    handleVolumeSlideChange(input, setVolumeLevel, setIsMuted, signalUserActivity);
    shakaSetVolume(playerMgr, input);
  };

  /**
   * A handler which will be triggered when VideoPlyer is closed
   * @param {Object} event event
   * @param {Boolean} isNotStillWatching 'true' when no response occurs after "Still Watching" prompt is ignored
   */
  const closeClickHandler = (event, isNotStillWatching = false) => {
    trackCloseClick(getMediaNodeMetaData, isDynatraceTrackingEnabled);
    trackMediaStop(getMediaNodeMetaData, isDynatraceTrackingEnabled);
    handleClosePlayer(
      isClosingPlayer,
      isFreshLogin,
      playerLoaded,
      isNotStillWatching,
      hasCalledStopContent,
      handleAVSStopContent,
      history
    );
  };

  const onFullScreenButtonHandler = (event) => {
    handleFullScreenToggle(onFullScreenClick, Document, videoWrapper, signalUserActivity);
  };

  /**
   * Set trick play on click
   * @param {*} event
   */
  const setProgress = (progress) => {
    const videoDuration = playerGetDuration(playerMgr);
    let videoPos = calculateVodPos(videoDuration, progress);
    if (formatSecondsToTimeDisplay(videoPos) > elapsedTime) {
      handleOnDemandProgressFastForward(
        progress,
        videoPos,
        isFFEnabled,
        setProgressStyle,
        updateVodProgress,
        seekPlayback,
        trackConvivaAppTrackerEvent,
        getMediaNodeMetaData,
        onPlaybackStateChange,
        translate,
        videoStarted
      );
    } else {
      handleOnDemandProgressRewind(
        progress,
        videoPos,
        isRWEnabled,
        setProgressStyle,
        updateVodProgress,
        seekPlayback,
        trackConvivaAppTrackerEvent,
        getMediaNodeMetaData,
        onPlaybackStateChange,
        translate,
        videoStarted
      );
    }
    signalUserActivity();
  };

  /*
    Seeks forward or backward to the set time
  */
  const seekPlayback = (seekTarget, appendCurrentPos = true) => {
    const { currentPos, videoDuration } = playerGetSeekParameters(playerMgr);
    let newPos = playerCalculateSeekPosition(currentPos, seekTarget, appendCurrentPos);
    if (newPos >= videoDuration) {
      newPos = playerSeekableEndPos(videoDuration);
    }
    playerHandleStaticSeeking(playerMgr, newPos);
  };

  const handleEpisodeCardClick = () => {
    telusConvivaAnalytics.onPlayNextProgram();
    trackConvivaAppTrackerEvent(ANALYTICS_EVENT_TYPES.UP_NEXT_START, getMediaNodeMetaData);
    loadNextProgram &&
      loadNextProgram(false, () => loadNextEpisodeCallback(false, LINK_INFO.PLAY, isDynatraceTrackingEnabled));
    setBingeWatchInProgress(true);
    setShowNextEpisodeCard(false);
    signalUserActivity();
    upNext.current = 1;
  };

  const skipToNextEpisode = () => {
    if (typeof isProgramChangedRef.current === "boolean") {
      isProgramChangedRef.current = true;
    }
    telusConvivaAnalytics.onPlayNextProgram();
    loadNextProgram &&
      loadNextProgram(false, () => loadNextEpisodeCallback(false, LINK_INFO.NEXT_EPISODE, isDynatraceTrackingEnabled));
    setBingeWatchInProgress(true);
    setShowNextEpisodeCard(false);
    signalUserActivity();
  };

  const cancelAutoplay = () => {
    stopAutoPlayHandler(autoPlayTimer, setShowNextEpisodeCard, signalUserActivity);
  };

  const onSubTitleChange = (event, shouldTrackAnalytics = true) => {
    shakaHandleSubtitleChange(
      isClosedCaptionEnabled,
      playerMgr,
      event,
      signalUserActivity,
      isDynatraceTrackingEnabled,
      shouldTrackAnalytics
    );
  };

  const onADToggleChange = (isADEnabled) => {
    playerHandleADToggleChange(
      isADEnabled,
      isDescribedVideoEnabled,
      playerMgr,
      signalUserActivity,
      describedVideo,
      isDynatraceTrackingEnabled
    );
  };

  const signalUserActivity = useCallback(() => {
    if (restartUserInactivityTimer) {
      restartUserInactivityTimer();
    }
  }, [restartUserInactivityTimer]);

  const mouseEventHandler =
    !(showNextEpisodeCard && !isSideNavOpen) && !showStillWatchingPrompt ? videoControlsMouseMove : undefined;

  const startTimeEpoch = startTime;

  // Duration variable
  let duration = playerGetDuration(playerMgr);

  const shouldDisplayForwardButton = () => {
    if (isPlayerControlsDisable || !isFFEnabled) {
      return false;
    }
    return true;
  };

  const shouldDisplayRewindButton = () => {
    if (isPlayerControlsDisable) return false;
    if (isRWEnabled) return true;
  };

  const episodeInfoString = () => {
    return generateEpisodeInfoString(nextProgram);
  };

  const shouldDisplayRestartButton = () => {
    if (isPlayerControlsDisable || !videoStarted) {
      // do not display the restart icon for live player in panic mode, or if on-demand player's stream is not started
      return false;
    }
    if (isOnDemandRestartButtonVisible(playbackType, true, isDynamicRecordingType.current)) {
      return true;
    }
    return false;
  };

  const handleCloseTooltip = () => {
    setIsTooltipVisible(false);
  };

  return (
    <>
      <PlayerWrapper
        videoWrapperRef={videoWrapper}
        mouseEventHandler={mouseEventHandler}
        videoControlsKeyUp={videoControlsKeyUp}
        handleVolume={handleVolume}
        playerMgr={playerMgr}
        onClickToggleState={onClickToggleState}
      >
        {showErrorOverlay && <PlaybackError isSideNavOpen={isSideNavOpen} />}
        {isFullScreen(document) && toastData && Object.values(toastData).some((value) => value !== null) && (
          <ToastNotification /> // rendering inside browser's top layer when app is in full screen mode
        )}
        <BackgroundImage showBgImage={showBgImage} bgImageSrc={bgImage} videoClassName={videoClassName} />
        {videoSource && (
          <ShakaPlayerContainer>
            <VideoPlayer
              ref={playerMgr}
              source={videoSource}
              width={1920}
              height={1080}
              playerConfig={{
                events: {
                  [VideoPlayerEvent.SOURCE_LOADING]: handleSourceLoadingEvent,
                  [VideoPlayerEvent.SOURCE_LOADED]: handleSourceLoadedEvent,
                  [VideoPlayerEvent.READY]: handleReadyEvent,
                  [VideoPlayerEvent.PLAYING]: handlePlayingEvent,
                  [VideoPlayerEvent.PAUSED]: handlePausedEvent,
                  [VideoPlayerEvent.PLAYBACK_FINISHED]: handlePlaybackFinishedEvent,
                  [VideoPlayerEvent.WARNING]: handleWarningEvent,
                  [VideoPlayerEvent.ERROR]: handlePlaybackError,
                  [VideoPlayerEvent.CAPTION_TRACK_ADDED]: handleCaptionTrackAddedEvent,
                  [VideoPlayerEvent.TIME_CHANGED]: handleTimeChangedEvent,
                  [VideoPlayerEvent.SEEKING]: handleSeekingEvent,
                  [VideoPlayerEvent.SEEKED]: handleSeekedEvent,
                  [VideoPlayerEvent.BUFFERING]: handleBufferingEvent,
                  [VideoPlayerEvent.BUFFERED]: handleBufferedEvent,
                  [VideoPlayerEvent.VIDEO_QUALITY_CHANGED]: handleVideoQualityChanged,
                },
                autoplay: true,
                // Buffer: large buffer for VOD since latency is not a concern
                bufferBehind: 30, // seconds of backward buffer to retain
                bufferingGoal: 60, // seconds ahead to buffer — large for uninterrupted playback
                rebufferingGoal: 2, // seconds needed in buffer before resuming — low for fast start
                maxDisabledTime: 30, // re-enable failed variants after 30s instead of permanently disabling
                // ABR: conservative — downgrade early, upgrade cautiously
                switchInterval: 5, // minimum seconds between ABR quality switches
                bandwidthDowngradeTarget: 0.9, // downgrade when bandwidth drops to 90% of current requirement
                bandwidthUpgradeTarget: 0.7, // upgrade when bandwidth reaches 70% of next level
                // Retry: survive transient CDN/network errors
                retryParameters: { maxAttempts: 5 }, // more retries before failing a segment request
                // Dynamic playback rate: slows playback on low buffer to prevent rebuffering
                vodDynamicPlaybackRate: true, // enable dynamic playback rate adjustment for VOD
                vodDynamicPlaybackRateLowBufferRate: 0.9, // slow to 0.9x when buffer is low
                vodDynamicPlaybackRateBufferRatio: 0.5, // buffer threshold ratio (50% of bufferingGoal) to trigger slowdown
                //preferredAudioCodecs: ["mp4a.40.2", "ac-3"], // Allows us to override the audio codec if passed in
              }}
            />
          </ShakaPlayerContainer>
        )}

        <GradientBackground
          showNextEpisodeCard={showNextEpisodeCard}
          isSideNavOpen={isSideNavOpen}
          showStillWatchingPrompt={showStillWatchingPrompt}
          showBgImage={showBgImage}
          videoControlsStyle={videoControlsStyle}
        />

        <ClosedCaption ccList={ccList} isSideNavOpen={isSideNavOpen} />

        <StillWatchingCloseIcon
          showNextEpisodeCard={showNextEpisodeCard}
          isSideNavOpen={isSideNavOpen}
          showStillWatchingPrompt={showStillWatchingPrompt}
          altText={translate("close")}
          closeClickHandler={closeClickHandler}
        />

        {(!showNextEpisodeCard || isSideNavOpen) && (
          <div className={videoControlsName} ref={videoCCContainerRef} style={videoControlsStyle}>
            <div className="playbackControl-top dis-flex">
              <div className="dis-flex align-right">
                <FullScreenToggle
                  isFullScreen={isFullScreen(Document)}
                  translate={translate}
                  onFullScreenButtonHandler={onFullScreenButtonHandler}
                />
                <SidebarToggle
                  isFullScreen={isFullScreen(Document)}
                  hideListChannel={hideListChannel}
                  isOnDemandType={true}
                  showListButton={showListButton}
                  translate={translate}
                  isRecording={false}
                  onClickHandler={() => handleListIconClick(telusConvivaAnalytics.onSwitchProgram)}
                />
              </div>
              <CloseButton translate={translate} closeClickHandler={closeClickHandler} />
            </div>
            <div className="playbackControl-metaData dis-flex">
              <div className="metadata">
                <div
                  className={
                    image ? "info dis-flex align-item-start justify-item-start" : "info dis-flex justify-item-start"
                  }
                >
                  <ChannelInfo
                    channelNumber={channelNumber}
                    type={type}
                    image={image}
                    onError={(e) => handleImageError(e, defaultChannelLogoIcon)}
                  />
                  <Details title={title} subTitle={subTitle} episodeInfo={null} />
                  <div className="dis-flex align-right pos-rel">
                    <VolumeControl
                      volumeRef={volumeRef}
                      onMouseEnter={() => {
                        if (userHasClick) volumeButtonHoverHandler();
                      }}
                      onMouseLeave={() => {
                        setVolumeVisibility(false);
                      }}
                      isMuted={isMuted}
                      volumeLevel={volumeLevel}
                      translate={translate}
                      volumeButtonRef={volumeButtonRef}
                      volumeButtonHandler={volumeButtonHandler}
                      volumeVisibility={volumeVisibility}
                      playerMgr={playerMgr}
                      onVolumeSlideChange={onVolumeSlideChange}
                      volumeBarRef={volumeBarRef}
                    />
                    <SettingsControl
                      settingsRef={settingsRef}
                      isCCAvailable={isCCAvailable}
                      isADAvailable={isADAvailable}
                      translate={translate}
                      settingButtonHandler={settingButtonHandler}
                      spinnerToggle={spinnerToggle}
                      settingsDropdownVisibility={settingsDropdownVisibility}
                      isClosedCaptionEnabled={isClosedCaptionEnabled}
                      onSubTitleChange={onSubTitleChange}
                      onADToggleChange={onADToggleChange}
                      isDescribedVideoEnabled={isDescribedVideoEnabled}
                      playerMgr={playerMgr}
                    />
                  </div>
                </div>
                <div className="progressBar-wrap">
                  <ProgressBar
                    progress={progressStyle}
                    bufferProgress={0}
                    onProgressClick={setProgress}
                    disabled={spinnerToggle}
                    playerMgr={playerMgr.current}
                    playerType={type}
                    elapsedTime={!Number.isNaN(elapsedTime) ? elapsedTime : ""}
                    duration={duration}
                    leftMargin={leftMargin.current}
                    showLiveProgress={false}
                    currentPlaybackTime={currentPlaybackTime}
                    endTime={endTime}
                    startTime={startTime}
                    isPauseLiveAllowed={isPauseLiveAllowed.current}
                    isOnDemandType={true}
                    checkIfPauseLiveAllowed={() => false}
                    isRestartedStream={isRestartedStream}
                    isLookbackStream={false}
                    isTimeShifting={isTimeShifting.current}
                    isSeeking={isSeeking.current}
                    isRestartTrickPlayAllowed={isRestartTrickPlayAllowed.current}
                    isPlayerControlsDisable={isPlayerControlsDisable}
                    startTimeEpoch={startTimeEpoch}
                    remainingTime={remainingTime}
                    userRecordingStopTime={null}
                    isRecording={false}
                    currentImageTrack={currentImageTrack}
                    isZoetropeEnabled={isVODZoetropeEnabled}
                  />
                </div>
              </div>
            </div>
            <div className="playbackControl-bottom dis-flex">
              <React.Fragment>
                <div className="dis-flex">
                  <RewindButton
                    shouldDisplayRewindButton={shouldDisplayRewindButton()}
                    translate={translate}
                    rewindButtonHandler={rewindButtonHandler}
                    spinnerToggle={spinnerToggle}
                  />
                  <PlayPauseButton
                    shouldDisplayPlayPauseButton={!isPlayerControlsDisable}
                    playPauseIcon={playPauseIcon}
                    translate={translate}
                    playPauseButtonHandler={playPauseButtonHandler}
                    playButtonRef={playButtonRef}
                    spinnerToggle={spinnerToggle}
                  />
                  <ForwardButton
                    shouldDisplayForwardButton={shouldDisplayForwardButton()}
                    isVOD={true}
                    translate={translate}
                    forwardButtonHandler={forwardButtonHandler}
                    spinnerToggle={spinnerToggle}
                  />
                </div>
                <div className="dis-flex align-right">
                  <RestartButton
                    shouldDisplayRestartButton={shouldDisplayRestartButton()}
                    translate={translate}
                    onClickHandler={() => startOverStream(false)}
                    spinnerToggle={spinnerToggle}
                  />
                  <NextEpisodeButton
                    nextProgram={nextProgram}
                    translate={translate}
                    skipToNextEpisode={skipToNextEpisode}
                    spinnerToggle={spinnerToggle}
                  />
                </div>
              </React.Fragment>
            </div>
            {/*Zeotrope Tooltip Logic */}
            {userProfile?.isLoggedIn && isVODZoetropeEnabled && isTooltipVisible && videoStarted && !isSafari && (
              <VODToolTip
                tooltipMessages={tooltipMessages}
                handleClose={handleCloseTooltip}
                isTooltipVisible={isTooltipVisible}
              />
            )}
          </div>
        )}

        {contentLockedOverlay}

        <NextEpisodeCard
          showNextEpisodeCard={showNextEpisodeCard}
          nextProgram={nextProgram}
          seriesDetails={seriesDetails}
          isSideNavOpen={isSideNavOpen}
          nextEpisodePoster={nextEpisodePoster}
          placeholder={placeholder}
          translate={translate}
          episodeInfoString={episodeInfoString}
          bingeWatchCard={bingeWatchCard}
          bingeWatchTimer={bingeWatchTimer}
          handleEpisodeCardClick={handleEpisodeCardClick}
          cancelAutoplay={cancelAutoplay}
        />
        {showStillWatchingPrompt && (
          <StillWatchingCard
            ref={stillWatchingCard}
            className={`still-watching ${isSideNavOpen ? "shrink" : ""}`}
            onButtonClick={() => {
              signalUserActivity();
              trackGenericAction(
                ANALYTICS_EVENT_TYPES.STILL_WATCHING,
                getMediaNodeMetaData(),
                isDynatraceTrackingEnabled
              );
            }}
            onCountdownComplete={() => closeClickHandler(null, true)}
          />
        )}
      </PlayerWrapper>
    </>
  );
}

VodPlayer.propTypes = {
  className: PropTypes.string,
  type: PropTypes.string.isRequired,
  streamInfo: PropTypes.shape({
    streamMode: PropTypes.string,
    manifestURL: PropTypes.string,
    playbackType: PropTypes.string,
    drmType: PropTypes.string,
    LA_URL: PropTypes.string,
    LA_CERT_URL: PropTypes.string,
    authToken: PropTypes.string,
  }),
  metadataInfo: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    image: PropTypes.string,
    duration: PropTypes.number,
    startTime: PropTypes.object,
  }),
  control: PropTypes.string,
  onPlaybackStateChange: PropTypes.func,
  nextProgramInfo: PropTypes.shape({
    nextProgram: PropTypes.object,
    nextEpisodePoster: PropTypes.string,
    loadNextProgram: PropTypes.func,
  }),
  showStillWatchingPrompt: PropTypes.bool,
  restartUserInactivityTimer: PropTypes.func,
  updateVodProgress: PropTypes.func,
  isReadyCallback: PropTypes.func,
  setIsReadyToChangeStreams: PropTypes.func,
  contentLockedOverlay: PropTypes.element,
};

VodPlayer.defaultProps = {
  className: "",
  nextProgramInfo: {},
  updateVodProgress: () => null,
};

export default VodPlayer;
